<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import CarList from '@/components/CarList.vue';
const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  view: {
    type: String,
    default: () => '',
  },
});

const activeTab = ref(props.data[0]?.id || '');

const currentTab = computed(() => {
  return props.data.find((tab) => tab.id === activeTab.value);
});

const allCarsUrl = computed(() => {
  return currentTab.value?.url || '';
});

const handleTabChange = async (tabId) => {
  activeTab.value = tabId;
};

const tabListElement = useTemplateRef('carsTabList');

const onTabListResize = () => {
  tabListElement.value?.updateInkBar();
};
onMounted(() => {
  window.addEventListener('resize', onTabListResize, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onTabListResize);
});
</script>
<template>
  <Tabs
    ref="carsTabsRef"
    v-model:value="activeTab"
    :scrollable="false"
    class="tabs"
    @tab-change="handleTabChange"
  >
    <div class="tab-headers">
      <TabList ref="carsTabList" class="tab-headers__list">
        <Tab
          v-for="tab in data"
          :key="tab.id"
          :value="tab.id"
          class="tab-title link-normal"
        >
          {{ tab.title }}
        </Tab>
      </TabList>
      <div v-if="allCarsUrl" class="tab-headers__button">
        <CTagDetect
          :to="allCarsUrl"
          class="tab-headers__button-link p-button-text button-icon-next link-normal"
          >{{ $t('header.all_cars') }}
          <CIcon
            class="button-icon-next-icon"
            name="arrow-red"
            width="16"
            height="16"
          />
        </CTagDetect>
      </div>
    </div>
    <TabPanels class="tab-content">
      <TabPanel v-for="tab in data" :key="tab.id" :value="tab.id">
        <CarList
          v-if="tab.children && tab.children.length > 0"
          :items="tab.children"
          :view="'compact'"
          :as-heading="false"
        />
        <div v-if="allCarsUrl" class="tab-content__button">
          <Button v-slot="slotProps" as-child rounded>
            <CTagDetect
              :to="allCarsUrl"
              :class="[
                slotProps.class,
                ' link-normal button-next-arrow button-black',
                `js-button-tabs-${tab.id}`,
              ]"
            >
              {{ $t('header.all_cars') }}
              <CIcon
                class="button-next-arrow__icon"
                name="arrow-white"
                width="16"
                height="16"
              />
            </CTagDetect>
          </Button>
        </div>
      </TabPanel>
    </TabPanels>
  </Tabs>
</template>

<style scoped lang="scss">
.tabs {
  display: flex;
  flex-direction: column;
  gap: 40px;
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 40);
  }
}
.tab {
  &-headers {
    background-color: $white;
    display: flex;
    position: sticky;
    top: 0;
    padding: 24px 12px 0 24px;
    z-index: 1;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(padding-top, 24);
      @include responsive-spacing(padding-right, 12);
      @include responsive-spacing(padding-left, 24);
    }
    &__button {
      display: grid;
      margin-left: auto;
    }
    &:deep(.p-tablist-tab-list) {
      justify-content: space-between;
      width: 100%;
    }
    &:deep(.p-tablist-tab) {
      flex: 1 1 auto;
      justify-content: center;
    }
  }
  &-title {
    border-bottom: 1px solid $divider;
    color: $on-surface-variant-2;
    cursor: pointer;
    position: relative;
    padding: 0 12px 14px;
    text-align: center;
    transition: color 0.2s ease;
    white-space: unset;
    width: 50%;
    @include media-breakpoint-up(sm) {
      padding: 17px 16px 15px;
      width: auto;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-bottom-width, 1);
      @include responsive-spacing(padding-top, 17);
      @include responsive-spacing(padding-right, 16);
      @include responsive-spacing(padding-left, 16);
      @include responsive-spacing(padding-bottom, 15);
    }
    &[data-p='active'] {
      color: $on-surface;
    }
  }
  &-content {
    display: flex;
    flex-direction: column;
    gap: 40px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 40);
    }
    &__empty {
      padding: 24px;
      text-align: center;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-bottom-left-radius, 4);
        @include responsive-spacing(padding, 24);
      }
    }
    &__button {
      background: linear-gradient(
        180deg,
        rgba(255, 255, 255, 0) 0%,
        $white 95%
      );
      border-bottom-left-radius: 4px;
      bottom: 0;
      position: sticky;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-bottom-left-radius, 4);
        @include responsive-spacing(padding, 24);
      }
      &-link {
        max-width: fit-content;
      }
    }
  }
}
.car {
  &__items {
    align-items: start;
    display: grid;
    flex-direction: column;
    gap: 56px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 56);
    }
  }
}
</style>
